import { useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sidebar } from "./Sidebar";

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="godo-app-bg min-h-screen">
      {mobileOpen && (
        <button
          type="button"
          aria-label={t("labels.close_menu")}
          className="fixed inset-0 z-40 bg-slate-900/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex min-h-screen">
        <aside
          className={`w-72 shrink-0 transform transition-transform duration-200 ease-out md:relative md:translate-x-0 ltr:left-0 rtl:right-0 ${
            mobileOpen
              ? "fixed inset-y-0 z-50 translate-x-0"
              : "max-md:fixed max-md:inset-y-0 max-md:z-50 max-md:hidden max-md:ltr:-translate-x-full max-md:rtl:translate-x-full"
          }`}
        >
          <Sidebar onNavigate={() => setMobileOpen(false)} />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 p-2 text-slate-600 md:hidden"
                  onClick={() => setMobileOpen((open) => !open)}
                  aria-expanded={mobileOpen}
                  aria-label={t("labels.open_menu")}
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden
                  >
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div>
                  <h1 className="text-base font-semibold text-godo-primary">
                    {t("labels.app_name")}
                  </h1>
                  <p className="text-xs text-slate-500">
                    {t("labels.session_active")}
                  </p>
                </div>
              </div>
            </div>
          </header>

          <main className="flex-1 px-2 py-2 lg:px-3">
            <div key={pathname} className="min-h-0">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
